import { MultiCurrencyWrapper } from 'components/MultiCurrencyWrapper';
import React from 'react';
import type { HourState } from 'redux/modules/hourStates.schema';
import type { Money } from 'types/money';
import { customFormatCurrency, formatCurrency } from 'utils/currency';
import type { CurrencyFormatOptions } from '../datatable.types';
import styles from './column.module.scss';
import { COLUMN_SIZE_SMALL } from './constants';
import HeaderWithMenu from './HeaderWithMenu';
import type { DatatableCellRenderer, DatatableColumnGetter } from './types';

interface MoneyParams {
  label?: string;
  currencyFormat?: CurrencyFormatOptions;
}

interface PartialRowData {
  hasMultipleCurrencies?: boolean | undefined;
  billable: string;
  state: HourState;
}

export const getMoneyColumn: DatatableColumnGetter<Money, MoneyParams, PartialRowData> = (
  id: string,
  { label, currencyFormat } = { label: 'Money' }
) => ({
  id,
  header: props => <HeaderWithMenu {...props} label={label} sortLabelType="number" alignRight />,
  size: COLUMN_SIZE_SMALL,
  minSize: COLUMN_SIZE_SMALL,
  maxSize: COLUMN_SIZE_SMALL,
  enableResizing: false,
  meta: {
    numeric: true,
    alignRight: true,
    currencyFormat: currencyFormat,
    money: true,
  },
  cell: props => <MoneyCell {...props} currencyFormat={currencyFormat} />,
});

const MoneyCell: DatatableCellRenderer<
  Money,
  { currencyFormat?: CurrencyFormatOptions },
  PartialRowData
> = ({ getValue, row, currencyFormat }) => {
  const money = getValue();

  if (!money || money.value == null) {
    return <span>—</span>;
  }

  const { value, currency } = money;
  const formattedCurrency = currencyFormat
    ? customFormatCurrency(value, currency, currencyFormat)
    : formatCurrency(currency, value, 2);

  if (value === 0) {
    return <span>—</span>;
  }

  const shouldStrikeThrough = row.original.billable === '-';

  return (
    <MultiCurrencyWrapper hasMultipleCurrencies={row.original.hasMultipleCurrencies}>
      <span className={shouldStrikeThrough ? styles.strikeThrough : ''}>{formattedCurrency}</span>
    </MultiCurrencyWrapper>
  );
};
